import * as Icons from '@ant-design/icons';
import { Button, Progress, Space, Table, Tag, Tooltip } from 'antd';
import { CourseNameLink } from 'components/user/CourseNameLink';
import {
  ENROLLMENT_STATUS,
  ENROLLMENT_STATUS_LABELS
} from 'constants/admin/enrollments';
import { EnrollmentStatusFilter } from 'components/user/EnrollmentStatusFilter';
import { HttpError, useNavigation } from '@refinedev/core';
import { ICourse } from 'interfaces/courses';
import { IEnrollment } from 'interfaces/enrollments';
import { TagField, TextField, useTable } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { show } from 'requests/certificates';
import { tagColorByStatus } from 'utils/statusColorMapping';
import { useState } from 'react';

interface Props {
  userId: string;
}

interface ActionsProps {
  enrollment: IEnrollment;
}

const Actions = ({ enrollment }: ActionsProps) => {
  const [loading, setLoading] = useState(false);

  const downloadCertificate = async () => {
    setLoading(true);

    try {
      const { url } = await show({ enrollmentId: enrollment.id });

      window.open(url, '_blank');
    } catch {}
    setLoading(false);
  };

  return (
    <div className="flex gap-2">
      <Space>
        <Button
          size="small"
          className="!flex items-center"
          onClick={downloadCertificate}
          loading={loading}
          icon={<Icons.DownloadOutlined />}
        >
          Download
        </Button>
      </Space>
    </div>
  );
};

const renderCourseType = (course?: ICourse) => {
  if (!course) {
    return '-';
  }

  return (
    <Space size={4} wrap>
      {course.kind === 'v1' ? (
        <Tag color="orange">V1 · Deprecado</Tag>
      ) : (
        <Tag>V2</Tag>
      )}
      {course.course_type === 'on_demand' && <Tag color="blue">Flex</Tag>}
    </Space>
  );
};

const EnrollmentTable = ({ userId }: Props) => {
  const navigate = useNavigation();
  const [status, setStatus] = useState<string | undefined>(
    ENROLLMENT_STATUS.ACTIVE
  );

  const { editUrl } = navigate;
  const { tableProps, setFilters, setCurrent } = useTable<
    IEnrollment,
    HttpError
  >({
    resource: 'courses/enrollments',
    queryOptions: {
      enabled: Boolean(userId)
    },
    syncWithLocation: false,
    filters: {
      permanent: [
        {
          field: 'filter[by_user_id]',
          operator: 'eq',
          value: userId
        }
      ],
      initial: [
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: ENROLLMENT_STATUS.ACTIVE
        }
      ]
    }
  });

  const handleStatusChange = (nextStatus?: string) => {
    setStatus(nextStatus);
    setCurrent(1);
    setFilters(
      [
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: nextStatus
        }
      ],
      'merge'
    );
  };

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <EnrollmentStatusFilter value={status} onChange={handleStatusChange} />

      <Table
        {...tableProps}
        rowKey="id"
        bordered
        scroll={{ x: true }}
        className="[&_tr:hover_.course-link-icon]:opacity-100"
      >
        <Table.Column<IEnrollment>
          dataIndex="course"
          key="course"
          title="Curso"
          render={course => (
            <CourseNameLink
              href={course?.id ? editUrl('courses', course.id) : undefined}
              name={course?.name}
            />
          )}
        />
        <Table.Column<IEnrollment>
          dataIndex="course"
          key="course_type"
          title="Tipo"
          render={course => renderCourseType(course)}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          render={status => (
            <TagField
              color={tagColorByStatus(status)}
              value={ENROLLMENT_STATUS_LABELS[status] || status}
            />
          )}
        />
        <Table.Column
          dataIndex="progress_percentage"
          key="progress_percentage"
          title="Progresso"
          render={progress => (
            <Tooltip title={`${progress}%`}>
              <Progress
                percent={progress}
                showInfo={false}
                strokeColor={{
                  '0%': '#0322e2',
                  '100%': '#08adff'
                }}
              />
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Início"
          render={createdAt => <TextField value={formatDate(createdAt, 'l')} />}
        />
        <Table.Column
          dataIndex="expires_at"
          key="expires_at"
          title="Expiração"
          render={expiresAt => (
            <TextField value={expiresAt && formatDate(expiresAt, 'l')} />
          )}
        />
        <Table.Column
          dataIndex="certificate"
          key="certificate"
          title="Emissão do certificado"
          render={certificate => (
            <TextField
              value={
                certificate?.issued_at && formatDate(certificate.issued_at, 'l')
              }
            />
          )}
        />
        <Table.Column<IEnrollment>
          title="Certificado"
          dataIndex="actions"
          render={(_, enrollment) => {
            return (
              <>
                {enrollment.certificate && <Actions enrollment={enrollment} />}
              </>
            );
          }}
        />
        <Table.Column<IEnrollment>
          title="Ações"
          dataIndex="actions"
          render={(_, enrollment) => {
            return (
              <Button
                icon={<Icons.EyeOutlined />}
                size="small"
                onClick={() =>
                  navigate.show(`course_enrollment`, enrollment.id, 'push')
                }
              />
            );
          }}
        />
      </Table>
    </Space>
  );
};

export default EnrollmentTable;
